// ============================================================
// FILE: _services/device-integration.service.ts
//
// Ported from EzovionOPDFrontend (Angular 20) core/services/
// device-integration.service.ts, converted to Angular 9 idioms:
// inject() -> constructor DI, signal()/computed() -> plain field +
// getters (Angular 9 has no Signals API; getters are re-evaluated
// on every change-detection pass, same effective behavior as the
// source's computed() here since these are cheap array scans).
// DeviceItem moved to @core/interfaces/device-integration/device-item.model
// (see that file) instead of being declared inline. No logic changes.
//
// Station-level Telemedicine Kit device fleet. Devices stay paired
// to this console across patients (this mirrors how BLE pairing
// actually works), so the connection state lives here — a single
// source of truth shared by the topbar badge and the Device
// Integration page, rather than per-patient state.
// ============================================================
import { forwardRef, Inject, Injectable } from '@angular/core';
import { X6ManagerService } from './aabo-x6/x6-manager.service';
import { ScopeManagerService } from './aabo-scope/scope-manager.service';
import { FetalDopplerManagerService } from './fetal-doppler/fetal-doppler-manager.service';
import { Ecg11ManagerService } from './ecg-standalone11/ecg-standalone11-manager.service';
import { StethoscopeManagerService } from './stethoscope/stethoscope-manager.service';
import { DeviceItem, DeviceMethod } from '../@core/interfaces/device-integration/device-item.model';
import { DeviceNotificationService } from './device-notification.service';

/** Stable id of the Aabo X6 6-in-1 vitals monitor — the device the guided auto-capture flow drives. */
export const AABO_X6_DEVICE_ID = 'aabo_x6';

/** Stable id of the Aabo Scope WiFi endoscope/otoscope — reuses the fleet's existing "General Examination Camera" slot, now backed by ScopeManagerService instead of the station-simulator. */
export const AABO_SCOPE_DEVICE_ID = 'exam_camera';

/** Stable id of the Fetal Doppler — reuses the fleet's pre-existing "Fetal Doppler" slot, now backed by FetalDopplerManagerService instead of the station-simulator. */
export const FETAL_DOPPLER_DEVICE_ID = 'fetal_doppler';

/** Stable id of ECG Standalone 11 (12-lead) — reuses the fleet's pre-existing, previously-unbacked "Portable ECG Device" slot, now backed by Ecg11ManagerService (Web Serial, not Web Bluetooth — see that service's header). Independent of AABO_X6_DEVICE_ID's own single-lead "ECG (Lead I)" vital, which is unaffected by this. */
export const ECG_STANDALONE11_DEVICE_ID = 'ecg_standalone11';

/** Stable id of the Aabo Stethoscope — backed by StethoscopeManagerService. Unlike every device above, it's not a custom BLE/Serial protocol: it's a USB-C/Bluetooth device the OS exposes as a plain system microphone (see that service's header), so "connecting" it means picking it from an enumerated audio-input list rather than a GATT/serial handshake. */
export const STETHOSCOPE_DEVICE_ID = 'stethoscope';

const DEFAULT_DEVICES: DeviceItem[] = [
  { id: AABO_X6_DEVICE_ID, name: 'Health Monitor', icon: 'fa-heartbeat', image: 'assets/images/devices/health-monitor.png', measures: 'SpO₂ · Pulse · Blood Pressure · Temperature · ECG · Glucose', mandatory: true, custom: false, connected: false, battery: null },
  { id: ECG_STANDALONE11_DEVICE_ID, name: 'ECG Standalone 11', icon: 'fa-wave-square', image: 'assets/images/devices/ecg.png', measures: '12-Lead ECG', mandatory: false, custom: false, connected: false, battery: null },
  { id: AABO_SCOPE_DEVICE_ID, name: 'General Examination', icon: 'fa-camera', image: 'assets/images/devices/general-examination.png', measures: 'Exam images (WiFi otoscope)', mandatory: false, custom: false, connected: false, battery: null },
  { id: FETAL_DOPPLER_DEVICE_ID, name: 'Fetal Doppler', icon: 'fa-water', image: 'assets/images/devices/fetal-doppler.png', measures: 'Fetal Heart Rate (FHR) · TOCO · Maternal HR', mandatory: false, custom: false, connected: false, battery: null },
  { id: STETHOSCOPE_DEVICE_ID, name: 'Digital Stethoscope', icon: 'fa-stethoscope', image: 'assets/images/devices/digital-stethoscope.png', measures: 'Heart & Lung Sounds (Audio)', mandatory: false, custom: false, connected: false, battery: null },
];

@Injectable({ providedIn: 'root' })
export class DeviceIntegrationService {
  private _devices: DeviceItem[] = DEFAULT_DEVICES.map(d => ({ ...d }));

  /** Was `devices` (a readonly signal) in source — call as a property, not a function: `deviceIntegrationService.devices`. */
  get devices(): DeviceItem[] {
    return this._devices;
  }

  get totalCount(): number {
    return this._devices.length;
  }

  get connectedCount(): number {
    return this._devices.filter(d => d.connected).length;
  }

  get mandatoryDevices(): DeviceItem[] {
    return this._devices.filter(d => d.mandatory);
  }

  get allMandatoryConnected(): boolean {
    return this.mandatoryDevices.every(d => d.connected);
  }

  // forwardRef: X6ManagerService/ScopeManagerService and this service are
  // all shared between the Triage and Device Integration lazy chunks via a
  // common webpack chunk; deferring the type reference until DI actually
  // resolves it avoids a "Cannot access '...' before initialization" error
  // tied to that chunk's load order.
  constructor(
    @Inject(forwardRef(() => X6ManagerService)) private x6: X6ManagerService,
    @Inject(forwardRef(() => ScopeManagerService)) private scope: ScopeManagerService,
    @Inject(forwardRef(() => FetalDopplerManagerService)) private fetalDoppler: FetalDopplerManagerService,
    @Inject(forwardRef(() => Ecg11ManagerService)) private ecg11: Ecg11ManagerService,
    @Inject(forwardRef(() => StethoscopeManagerService)) private stethoscope: StethoscopeManagerService,
    private readonly deviceNotifications: DeviceNotificationService,
  ) {
    // Aabo X6 is a real device in this fleet — everything else here is
    // still the station-simulator, except Aabo Scope below. Mirror the
    // SDK's own connection state onto its fleet entry so the topbar badge
    // and the Device Integration page both reflect the actual Bluetooth
    // link, not a toggle.
    //
    // Root-singleton service (providedIn: 'root') subscribing in its own
    // constructor and never unsubscribing is intentional here, same as
    // source — the subscription lives exactly as long as the service does.
    this.x6.state$.subscribe(state => {
      this.applyConnectionState(AABO_X6_DEVICE_ID, state.status === 'connected', d => ({ battery: state.batteryPercent ?? d.battery }));
    });

    // Aabo Scope is "connected" once the scope-bridge process is reachable
    // AND it reports the device itself as connected (not just the bridge
    // socket) — mirrors the analogous X6 subscription above.
    this.scope.state$.subscribe(state => {
      this.applyConnectionState(AABO_SCOPE_DEVICE_ID, state.wsStatus === 'connected' && state.deviceConnected, d => ({ battery: state.batteryPercent ?? d.battery }));
    });

    // Fetal Doppler's battery is 0-4 on the device's own scale (see
    // FetalDopplerState's doc comment) -- converted to a percent here so
    // the fleet card's battery display is consistent across every device,
    // mirroring X6/Scope's own already-percent batteryPercent fields.
    this.fetalDoppler.state$.subscribe(state => {
      this.applyConnectionState(FETAL_DOPPLER_DEVICE_ID, state.status === 'connected', d => ({ battery: state.batteryLevel !== undefined ? Math.round((state.batteryLevel / 4) * 100) : d.battery }));
    });

    // ECG Standalone 11 -- entirely independent transport (Web Serial, not
    // Web Bluetooth) and its own manager/state, mirrored onto the fleet the
    // same way as every other real device above.
    this.ecg11.state$.subscribe(state => {
      this.applyConnectionState(ECG_STANDALONE11_DEVICE_ID, state.connection === 'connected', d => ({ battery: state.batteryPercent ?? d.battery }));
    });

    // Stethoscope -- "connected" just means a device has been picked from
    // the enumerated audio-input list (see StethoscopeManagerService's
    // header); there's no GATT/serial link to reflect and no battery
    // concept for a plain microphone, so battery stays null always.
    this.stethoscope.state$.subscribe(state => {
      this.applyConnectionState(STETHOSCOPE_DEVICE_ID, !!state.selectedDeviceId && state.status !== 'error');
    });
  }

  /**
   * Single place every device's state$ subscription above funnels through:
   * merges the new connected flag (plus any device-specific extra fields,
   * e.g. battery) into the fleet, and -- on a true -> false transition only,
   * never on the initial 'idle'/'disconnected' state a manager starts in --
   * raises a header-bell notification. Comparing against the fleet's own
   * previous entry (rather than each manager's separate previous state$
   * value) keeps this the one spot that decides "was this actually a
   * disconnect", regardless of which manager's state shape triggered it.
   */
  private applyConnectionState(id: string, connected: boolean, extra: (d: DeviceItem) => Partial<DeviceItem> = () => ({})): void {
    const current = this._devices.find(d => d.id === id);
    const wasConnected = !!current?.connected;
    this._devices = this._devices.map(d => d.id === id ? { ...d, ...extra(d), connected } : d);
    if (wasConnected && !connected) {
      this.deviceNotifications.notify(id, current?.name ?? id);
    }
  }

  toggleDevice(id: string): void {
    if (id === AABO_X6_DEVICE_ID) {
      // Real Bluetooth connect/disconnect — must run synchronously from the
      // click handler (Web Bluetooth's device chooser requires a same-tick
      // user gesture). The fleet entry updates itself via the state$
      // subscription above, not here.
      const current = this._devices.find(d => d.id === id);
      if (current?.connected) this.x6.disconnect(); else this.x6.connect();
      return;
    }
    if (id === AABO_SCOPE_DEVICE_ID) {
      // Connecting needs the WiFi-picker UI (scan/select network/enter
      // password), so the "connect" gesture is handled by
      // ScopeConnectModalComponent, not here. Disconnect has no such UI
      // requirement, so it's handled directly like X6's.
      const current = this._devices.find(d => d.id === id);
      if (current?.connected) this.scope.disconnect();
      return;
    }
    if (id === FETAL_DOPPLER_DEVICE_ID) {
      // Real Bluetooth connect/disconnect — same same-tick-user-gesture
      // requirement as X6's branch above (Web Bluetooth's device chooser).
      const current = this._devices.find(d => d.id === id);
      if (current?.connected) this.fetalDoppler.disconnect(); else this.fetalDoppler.connect();
      return;
    }
    if (id === ECG_STANDALONE11_DEVICE_ID) {
      // Web Serial's device chooser has the same same-tick-user-gesture
      // requirement as Web Bluetooth's above, even though it's a different
      // browser API entirely — see Ecg11ManagerService's header.
      const current = this._devices.find(d => d.id === id);
      if (current?.connected) this.ecg11.disconnect(); else void this.ecg11.connect();
      return;
    }
    if (id === STETHOSCOPE_DEVICE_ID) {
      // Connecting needs the device-picker UI (choose which enumerated
      // audio input is the stethoscope), so the "connect" gesture is
      // handled by StethoscopeConnectModalComponent, not here — same
      // reasoning as Aabo Scope's WiFi-picker branch above. Disconnect has
      // no such UI requirement, so it's handled directly.
      const current = this._devices.find(d => d.id === id);
      if (current?.connected) this.stethoscope.disconnect();
      return;
    }
    this._devices = this._devices.map(d => d.id === id
      ? { ...d, connected: !d.connected, battery: !d.connected ? 70 + Math.floor(Math.random() * 28) : null }
      : d);
  }

  disconnectAll(): void {
    this.x6.disconnect();
    this.scope.disconnect();
    this.fetalDoppler.disconnect();
    this.ecg11.disconnect();
    this.stethoscope.disconnect();
    this._devices = this._devices.map(d => ({ ...d, connected: false, battery: null }));
  }

  addDevice(name: string, method: DeviceMethod, deviceId: string): void {
    const id = 'dev-' + Math.floor(Math.random() * 100000);
    this._devices = [...this._devices, {
      id, name, icon: 'fa-microchip', measures: 'Not yet mapped to a vital',
      mandatory: false, custom: true, connected: true,
      battery: 85 + Math.floor(Math.random() * 10), method, deviceId: deviceId || undefined,
    }];
  }

  renameDevice(id: string, name: string): void {
    this._devices = this._devices.map(d => (d.id === id ? { ...d, name } : d));
  }

  /** Returns false (and leaves the fleet untouched) for built-in devices — only custom ones can be removed. */
  deleteDevice(id: string): boolean {
    const target = this._devices.find(d => d.id === id);
    if (!target || !target.custom) return false;
    this._devices = this._devices.filter(d => d.id !== id);
    return true;
  }
}
